import moment from 'moment'
import { uuidv7 } from 'posthog-js/lib/src/uuidv7'
import { getCompEndMonth, getFiscalYear } from '~/composables/financials-grid-commons'
import { getLevelIdForMethod } from '~/composables/planning-helpers'
import { getSelectedLogicId, getValuesForSeasKey, setSelectedLogic } from '~/composables/seasonality-helpers'
import { type InputManualMap, MethodologyEnum, type PlanPosItem, type SeasonalityMap } from '~/protos/gen/stcs_comp_plan_cfg.entity'
import { StcsDisplayValueTypeEnum, StcsFpnaTypeEnum } from '~/protos/gen/stcs_enums.entity'
import type { CompPlanCfgCalcRequest } from '~/protos/gen/stcs_wapp_svc_comp_plan'
import { useCtxStore } from '~/stores/ctx.store'
import { createPlanningViewStore } from '~/stores/planningview.store'
import { getInvertSumValueFromRowList } from './planning-helpers'
import { type PlanningRowData, createValidInputManualMap } from './unified-edit-api'

export function createAnnualGrowthPosEntry(row: PlanningRowData, payload: CompPlanCfgCalcRequest, isStatusUpdate = false): PlanPosItem {
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const selectedPlan = planningStore.selectedPlanState

  // Find direct children with rowClass='selected-method-row' by checking parent row_id
  // Use second-to-last element in path_level to identify parent (works with any filter state)
  const BaseRows = planningStore.selectedPlanState?.pnlList.filter((r) => {
    if (r.rowClass !== 'selected-method-row') return false
    const parentRowId = r.path_level[r.path_level.length - 2]
    return parentRowId === row.row_id
  }) as PlanningRowData[]

  if (!selectedPlan || BaseRows.length < 2) {
    throw new Error('Selected plan not found or insufficient base rows for annual growth calculation')
  }

  const annualRow1 = BaseRows[0]
  const annualRow2 = BaseRows[1]

  const inputMap = createValidInputManualMap(annualRow1)
  const inputMap2 = createValidInputManualMap(annualRow2)

  // Get base amount key e.g. '2023-05' for May 2023
  const baseAmountKey = moment(selectedPlan?.fpna_begin_date_string, 'YYYY-MM').subtract(1, 'months').format('YYYY-MM')

  // Extract base amount value from inputMap using baseAmountKey
  const baseAmountValue = inputMap2[baseAmountKey]?.manual_number_input || 0

  // Create growth map for years (this would typically be populated based on user input)
  const growthMap: Record<string, number> = generateAnnualGrowthGrowthMap(annualRow2, inputMap2)

  // Get invert_sum_value from row options (can be -1 or 1)
  const invertSumValue = row.options?.invert_sum_value as number

  // Multiply each entry by invertSumValue
  const invertedGrowthMap = Object.fromEntries(Object.entries(growthMap).map(([year, value]) => [year, value * invertSumValue]))

  // Get seasonality key (this would typically be based on the row's seasonality configuration)
  const selectedLogicId = getSelectedLogicId(row.row_id)
  const entry = planningStore.seas_entry_map[row.row_id]

  const isCustom = entry?.logic_key === 'custom'
  // Use shared logic key: 'evenly', 'l12m', or actual custom ID (e.g., 'custom_1234567890')
  const seasonalityKey = isCustom ? selectedLogicId : entry?.logic_key === 'l12m' ? `l12m_${uuidv7()}` : 'evenly'

  handleSeasonalityLogicForPayload(row, payload, seasonalityKey)

  // Get pos_res_plan_map and pos_res_growth_map from cache only when isStatusUpdate is true
  let pos_res_plan_map = {}
  let pos_res_growth_map = {}
  if (isStatusUpdate && selectedPlan) {
    const parentRowId = row.path_level[row.path_level.length - 2]
    const cached = planningStore.planConfigCache[selectedPlan.id || '']?.stcs_comp_plan_cfg?.pnl_map?.[parentRowId]?.pos_map?.[row.row_id]?.annually_growth_rate
    pos_res_plan_map = cached?.pos_res_plan_map || {}
    pos_res_growth_map = cached?.pos_res_growth_map || {}
  }

  return {
    fstmt_type: row.fstmt_type,
    pos_logic_type: MethodologyEnum.ANNUALLY_GROWTH_RATE,
    display_value_type: StcsDisplayValueTypeEnum.SDVTE_PERCENTAGE,
    annually_growth_rate: {
      pos_input_base_amount: baseAmountValue,
      pos_input_seas_key: seasonalityKey,
      pos_input_ytg_map: inputMap, // Year-to-go map with manual inputs
      pos_input_growth_map: invertedGrowthMap, // Growth percentages by year
      pos_res_growth_map: pos_res_growth_map, // Populated from cache when isStatusUpdate is true
      pos_res_plan_map: pos_res_plan_map, // Populated from cache when isStatusUpdate is true
    },
    is_struct_item: false, // Handle by the backend only
    pos_index: 0,
    status: undefined, // Handle by the backend only
  }
}

export function generateAnnualGrowthGrowthMap(_row: PlanningRowData, inputMap?: Record<string, InputManualMap>): Record<string, number> {
  // Initialize growth map
  let growthMap: Record<string, number> = {}

  if (inputMap) {
    // Map input values to their target fiscal years for annual growth
    growthMap = mapInputValuesToTargetFiscalYears(inputMap)
  }

  return growthMap
}

export const createAnnualGrowthRemainingPositions = (row: PlanningRowData, method: MethodologyEnum): PlanningRowData[] => {
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const selectedPlan = planningStore.selectedPlanState
  if (!selectedPlan) return []

  const parentPath = row.path_level

  const growthRow1Id = `${row.row_id}_${getLevelIdForMethod(method)}_1`
  const growthRow1Data = {
    ...row,
    row_id: growthRow1Id,
    path_level: [...parentPath, growthRow1Id],
    level_id: `${getLevelIdForMethod(method)}_1`,
    rowClass: 'selected-method-row',
    fpna_type: StcsFpnaTypeEnum.SFT_LEVEL_2,
    ref_id_mirror: undefined,
    options: row?.options,
    selectedMethod: undefined,
    is_position_row: false,
  }

  const growthRow2Id = `${row.row_id}_${getLevelIdForMethod(method)}_2`
  const growthRow2Data = {
    ...row,
    row_id: growthRow2Id,
    path_level: [...parentPath, growthRow2Id],
    level_id: `${getLevelIdForMethod(method)}_2`,
    rowClass: 'selected-method-row',
    fpna_type: StcsFpnaTypeEnum.SFT_LEVEL_2,
    ref_id_mirror: undefined,
    options: row?.options,
    selectedMethod: undefined,
    is_position_row: false,
  }

  const rowsToAdd = [growthRow1Data, growthRow2Data]

  return rowsToAdd
}

export const handleAnnualGrowthPositions = (positionKey: string, planPosition: PlanPosItem) => {
  const selectedMethodRowKey1 = `${positionKey}_${getLevelIdForMethod(MethodologyEnum.ANNUALLY_GROWTH_RATE)}_1`
  const selectedMethodRowKey2 = `${positionKey}_${getLevelIdForMethod(MethodologyEnum.ANNUALLY_GROWTH_RATE)}_2`
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)

  const selectedPlan = planningStore.selectedPlanState
  if (!selectedPlan) return

  // Get invert_sum_value by traversing the row hierarchy
  const rowList = selectedPlan.pnlList
  const invertSumValue = getInvertSumValueFromRowList(positionKey, rowList)
  const seas_key = planPosition.annually_growth_rate?.pos_input_seas_key || ''
  const valuesMap = selectedPlan.valuesMapPnl || {}

  // Extract the base amount and input maps from the plan position
  const baseAmount = planPosition.annually_growth_rate?.pos_input_base_amount
  const ytgMap = planPosition.annually_growth_rate?.pos_input_ytg_map || {}
  const growthMap = planPosition.annually_growth_rate?.pos_input_growth_map || {}

  // Convert YTG map to values map format
  const ytgInputMap = Object.fromEntries(Object.entries(ytgMap).map(([date, { manual_number_input }]) => [date, manual_number_input]))

  // Sort the input map by date keys to ensure chronological order
  const sortedYtgEntries = Object.entries(ytgInputMap).sort(([dateA], [dateB]) => dateA.localeCompare(dateB))

  const planBeginDateStr = selectedPlan.fpna_begin_date_string // e.g., "2028-01"

  if (!planBeginDateStr) return

  // Get base amount key (month before plan begins)
  const baseAmountKey = moment(planBeginDateStr, 'YYYY-MM').subtract(1, 'months').format('YYYY-MM')

  // Create the values map with base amount and YTG values
  valuesMap[selectedMethodRowKey1] = { values_map: { ...Object.fromEntries(sortedYtgEntries) } }
  valuesMap[selectedMethodRowKey2] = {
    values_map: {
      ...(baseAmountKey && baseAmount !== undefined ? { [baseAmountKey]: baseAmount } : {}),
      ...Object.fromEntries(Object.entries(mapTargetFiscalYearsToInputValues(growthMap)).map(([key, value]) => [key, value * invertSumValue])),
    },
  }
  setSelectedLogic(positionKey, seas_key) // Ensure seasonality entry is created

  selectedPlan.valuesMapPnl = valuesMap
}

/**
 * Build seasonality payload using helper function from seasonality-helpers.ts
 */
const handleSeasonalityLogicForPayload = (row: PlanningRowData, payload: CompPlanCfgCalcRequest, seasonalityKey: string): void => {
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const selectedPlan = planningStore.selectedPlanState
  if (!selectedPlan) return

  // Use the helper function to get seasonality values for the payload
  const seasInputMap = getValuesForSeasKey(row.row_id, seasonalityKey)

  if (payload.comp_plan_cfg && !payload.comp_plan_cfg.seas_map?.[seasonalityKey]) {
    payload.comp_plan_cfg.seas_map = {
      ...payload.comp_plan_cfg.seas_map,
      [seasonalityKey]: {
        seas_input_map: seasInputMap,
        seas_res_map: {},
      },
    }
  }
}

export const createInitialSeasonalityEntryWhileProcessingResponse = (seas_map: { [key: string]: SeasonalityMap }): void => {
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const selectedPlan = planningStore.selectedPlanState
  if (!selectedPlan) return

  // Process each seas_map entry and update seas_entry_map
  Object.entries(seas_map)
    .filter(([keyWithSuffix]) => keyWithSuffix.includes('_'))
    .forEach(([keyWithSuffix, entry]) => {
      const parts = keyWithSuffix.split('_')
      const suffix = parts.pop() // e.g., '132123'
      const key = parts.join('_') // e.g., 'custom'

      // biome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
      if (!suffix || !entry.seas_input_map) {
        return // Skip invalid keys or missing input map
      }

      // Determine the logic type from the key
      const isL12m = keyWithSuffix.includes('l12m')
      const isCustom = keyWithSuffix.startsWith('custom_')
      const logicKey = isCustom ? 'custom' : isL12m ? 'l12m' : 'evenly'

      // Set the selection using the new API
      // For custom logics, we use the full keyWithSuffix as the seas_key
      // For evenly/l12m, we use the keyWithSuffix as the seas_key for backend reference
      setSelectedLogic(key, isCustom ? keyWithSuffix : logicKey)
    })
}

// Helper function to get plan fiscal year range
function getPlanFiscalYearRange(selectedPlan: { fpna_begin_date_string?: string; fpna_end_date_string?: string }, fyeMonth: number) {
  const planBeginDateStr = selectedPlan.fpna_begin_date_string
  const planEndDateStr = selectedPlan.fpna_end_date_string

  if (!planBeginDateStr) {
    throw new Error('Plan begin date not found')
  }

  if (!planEndDateStr) {
    throw new Error('Plan end date not found')
  }

  const [beginYear, beginMonth] = planBeginDateStr.split('-').map(Number)
  const [endYear, endMonth] = planEndDateStr.split('-').map(Number)

  const beginDate = new Date(beginYear, beginMonth - 1, 1)
  const endDate = new Date(endYear, endMonth - 1, 1)

  return {
    beginFiscalYear: getFiscalYear(beginDate, fyeMonth),
    endFiscalYear: getFiscalYear(endDate, fyeMonth),
  }
}

function mapInputValuesToTargetFiscalYears(inputMap: Record<string, InputManualMap>) {
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const planState = planningStore.selectedPlanState

  if (!planState) {
    throw new Error('No selected plan state found in planning store')
  }

  const selectedPlan = planningStore.selectedPlan
  if (!selectedPlan) {
    throw new Error('No selected plan found')
  }

  // Get fiscal year-end month from company data
  const fyeMonth = getCompEndMonth()
  const planBeginDateStr = selectedPlan.fpna_begin_date_string

  // Subtract one month from planBeginDateStr and compare with fyeMonth
  const lastActualDate = moment(planBeginDateStr, 'YYYY-MM').subtract(1, 'months')
  const isTrue = lastActualDate.format('MM') === String(fyeMonth).padStart(2, '0')

  // Get fiscal year range for the plan
  const { beginFiscalYear, endFiscalYear } = getPlanFiscalYearRange(selectedPlan, fyeMonth)

  const sortedInputEntries = Object.entries(inputMap).sort(([dateA], [dateB]) => dateA.localeCompare(dateB))

  // Get base amount key (month before plan begins)
  const baseAmountKey = moment(planBeginDateStr, 'YYYY-MM').subtract(1, 'months').format('YYYY-MM')

  // Filter out base amount entry - remaining entries are for growth years
  const validinputMapEntries = sortedInputEntries.filter(([monthKey]) => monthKey !== baseAmountKey)

  // First, determine the number of fiscal years we need to handle
  const numberOfFiscalYears = endFiscalYear - beginFiscalYear + (isTrue ? 1 : 0)

  const monthKeyMap: Record<string, number | undefined> = {}

  const startDate = moment(planBeginDateStr, 'YYYY-MM')

  for (let i = 0; i < numberOfFiscalYears; i++) {
    const monthKey = startDate.clone().add(i, 'months').format('YYYY-MM')

    // Set value from inputMap if it exists for this month, otherwise undefined
    const inputEntry = validinputMapEntries.find(([key]) => key === monthKey)
    monthKeyMap[monthKey] = inputEntry ? inputEntry[1].manual_number_input : undefined
  }

  const growthMap: Record<string, number> = {}

  Object.entries(monthKeyMap).forEach(([_monthKey, value], index) => {
    const targetFiscalYear = beginFiscalYear + index + (isTrue ? 0 : 1) // conditionally +1 because growth applies to the following fiscal year if not isTrue

    if (targetFiscalYear <= endFiscalYear && value !== undefined) {
      growthMap[targetFiscalYear.toString()] = value
    }
  })

  return growthMap
}

function mapTargetFiscalYearsToInputValues(growthMap: Record<string, number>): Record<string, number> {
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const planState = planningStore.selectedPlanState

  if (!planState) {
    throw new Error('No selected plan state found in planning store')
  }

  const selectedPlan = planningStore.selectedPlan
  if (!selectedPlan) {
    throw new Error('No selected plan found')
  }

  if (!selectedPlan.fpna_begin_date_string) {
    throw new Error('Plan begin date not found')
  }

  const fyeMonth = getCompEndMonth()
  const planBeginDateStr = selectedPlan.fpna_begin_date_string

  // Subtract one month from planBeginDateStr and compare with fyeMonth
  const lastActualDate = moment(planBeginDateStr, 'YYYY-MM').subtract(1, 'months')
  const isTrue = lastActualDate.format('MM') === String(fyeMonth).padStart(2, '0')

  const { beginFiscalYear, endFiscalYear } = getPlanFiscalYearRange(selectedPlan, fyeMonth)

  // Calculate total number of growth years (excluding current fiscal year)
  const numberOfFiscalYears = endFiscalYear - beginFiscalYear + (isTrue ? 1 : 0)

  const monthKeyMap: Record<string, number | undefined> = {}
  const startDate = moment(planBeginDateStr, 'YYYY-MM')

  for (let i = 0; i < numberOfFiscalYears; i++) {
    const monthKey = startDate.clone().add(i, 'months').format('YYYY-MM')

    const targetFiscalYear = (beginFiscalYear + i + (isTrue ? 0 : 1)).toString() // conditionally +1 because growth applies to the following fiscal year if not isTrue

    monthKeyMap[monthKey] = growthMap[targetFiscalYear]
  }

  // Filter out undefined values to return only defined entries
  const result: Record<string, number> = {}
  Object.entries(monthKeyMap).forEach(([key, value]) => {
    if (value !== undefined) {
      result[key] = value
    }
  })

  return result
}
